import { readSaved, save } from './gameDeck';
import { v4 as uuidv4 } from 'uuid';
import { cleanIdentity } from './playerIdentity';

export const TV_KEY = 'diyt-tv-screen-v1';
export const ACTIVE_ROOM = 'diyt-host-room-v1';
export const PENDING_TV = 'diyt-pending-tv-v1';
export const CONTROLLER_TAB = uuidv4();
export const displayKey = code => `diyt-display-${code}`;
export const turnKey = session => `${session.currentCardNumber}:${session.currentPrompt}`;
export function notifyCreativeChange() { window.dispatchEvent(new Event('creative-turn')); }
export function canControlTurn(code, expected) {
  const latest = readSaved(code + '_data', {});
  if (turnKey(latest) !== turnKey(expected) || latest.currentCardType !== expected.currentCardType || latest.completed) { notifyCreativeChange(); return false; }
  save(`diyt-controller-${code}`, CONTROLLER_TAB);
  return true;
}
export function publicDisplay(code, route, session, players = []) {
  const base = { roomCode: code };
  if (['play', 'results'].includes(route) && session.completed) return { ...base, phase: 'results', scores: (session.readyPlayers || []).slice(0, 40).map(p => ({ name: p.name, score: p.score || 0, ...cleanIdentity(p) })).sort((a, b) => b.score - a.score) };
  if (route !== 'play' || !session.currentPrompt) return { ...base, phase: 'waiting', players: players.slice(0, 40).map(p => ({ name: p.name || 'Joining…', ready: p.isReady === true, ...cleanIdentity(p) })) };
  const phase = session.currentCardType === 'guess' ? 'guess' : 'prompt';
  const snapshot = { ...base, phase, prompt: session.currentPrompt, names: session.currentPlayerNames || [], turn: session.currentCardNumber, total: (session.orderOfPrompts?.length || 0) * 2 };
  snapshot.people = snapshot.names.map(name => ({ name, ...cleanIdentity(session.readyPlayers?.find(p => p.name === name)) }));
  if (phase === 'guess' && session.revealedTurn === turnKey(session)) {
    snapshot.revealed = true;
    const author = session.readyPlayers?.find(p => [...(p.unusedPrompts || []), ...(p.usedPrompts || [])].includes(session.currentPrompt));
    snapshot.author = author?.name || 'Someone at your table';
    snapshot.authorIdentity = cleanIdentity(author);
  }
  return snapshot;
}
export async function displayRequest(route, body, signal) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort);
  if (signal?.aborted) controller.abort();
  const timeout = setTimeout(() => controller.abort(), 12000);
  try {
    const origin = process.env.REACT_APP_CREATIVE_API_ORIGIN || 'https://power.drinkifyouthink.com';
    const response = await fetch(`${origin}/api/library/creative/${route}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal, cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer' });
    const result = await response.json().catch(() => ({ error: 'TV connection is unavailable. Try again.' }));
    if (!response.ok) { const error = new Error(result.error); error.status = response.status; throw error; }
    return result;
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('Connection timed out. Check your internet and try again.');
    throw error;
  } finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
}
export async function ensureDisplay(code, signal) {
  let channel = readSaved(displayKey(code), null);
  if (!channel || channel.expiresAt < Date.now()) {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    channel = { id: crypto.randomUUID(), token: btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''), expiresAt: Date.now() + 12 * 60 * 60 * 1000 };
    save(displayKey(code), channel);
  }
  await displayRequest('create', { ...channel, snapshot: { roomCode: code, phase: 'waiting' } }, signal);
  return channel;
}
export function hostedRooms() {
  const active = readSaved(ACTIVE_ROOM, null);
  const rooms = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!/^\d{4}_data$/.test(key)) continue;
    const session = readSaved(key, null);
    if (session?.isHost && Date.now() - Date.parse(session.creationTimestamp) < 86400000) rooms.push({ code: key.slice(0, 4), route: active?.code === key.slice(0, 4) ? active.route : session.completed ? 'results' : session.orderOfPrompts?.length ? 'play' : 'enter-prompts' });
  }
  return rooms.sort((a, b) => (b.code === active?.code) - (a.code === active?.code));
}
